<template>
  <div id="app">
    <header class="header">
      <img src="./assets/logo.svg" alt="BKMS" class="app-title" style="filter: brightness(0) invert(1); height: 28px;">
      
      <!-- バーガーメニューボタン -->
      <div class="burger-menu-container">
        <button class="burger-button" @click.passive="toggleMenu">
          <span class="burger-line"></span>
          <span class="burger-line"></span>
          <span class="burger-line"></span>
        </button>
        
        <!-- ドロップダウンメニュー -->
        <div class="dropdown-menu" v-show="isMenuOpen">
          <ul class="menu-list">
            <li class="menu-item" @click.passive="exportData">データエクスポート</li>
            <li class="menu-item" @click.passive="showImportDialog">データインポート</li>
            <li class="menu-item" @click.passive="resetAllTiles">データ全削除</li>
            <li class="menu-item" @click.passive="showAboutPage">BKMSについて</li>
            <li class="menu-item" @click.passive="showPrivacyPolicy">プライバシーポリシー</li>
            <!-- <li class="menu-item" @click.passive="showTop50Modal = true">TOP20を表示</li> -->
          </ul>
        </div>
      </div>
    </header>
    
    <main class="main-content">
      <!-- 検索コンテナ - 統合デザイン -->
      <div class="search-container">
        <div class="integrated-search-box">
          <div class="search-engine-select-container">
            <select v-model="searchEngine" class="search-engine-select">
              <option value="google">Google</option>
              <option value="bing">Bing</option>
              <option value="chatgpt">ChatGPT</option>
            </select>
            <div class="select-arrow">▼</div>
          </div>
          <input 
            type="text" 
            v-model="searchQuery" 
            class="search-input"
            placeholder="検索キーワードを入力（Enterで検索）"
            @keyup.enter="executeSearch"
          />
        </div>
      </div>
      
      <!-- タイル一覧 (draggableに変更) -->
      <div class="tiles-container-wrapper">
        <draggable 
          v-model="bookmarks" 
          class="tiles-container"
          item-key="id"
          :animation="200"
          ghost-class="ghost-tile"
          @start="onDragStart"
          @end="onDragEnd"
          :delay="0"
          :delayOnTouchOnly="true"
          :disabled="isMobile"
        >
          <template #item="{element}">
            <BookmarkTile 
              :name="element.name"
              :icon-url="element.iconUrl || '/bookmark.svg'"
              :icon-color="'#AAA'"
              :backgroundColor="element.color || '#f0f0f0'"
              :isEmpty="element.isEmpty"
              :isMobile="isMobile"
              :url="element.url"
              @click="handleTileClick(element)"
              @edit="editTileContent(element)"
              @delete="clearTileContent(element)"
            />
          </template>
        </draggable>
      </div>
    </main>
    
    <!-- モーダルコンポーネントを追加 -->
    <BookmarkModal 
      :isOpen="isModalOpen" 
      :editTile="currentEditTile"
      @close="closeModal"
      @save="saveBookmark"
    />

    <!-- インポート用の非表示ファイル入力 -->
    <input 
      type="file" 
      ref="fileInput" 
      @change="handleFileImport" 
      accept=".json" 
      style="display: none"
    />
    
    <!-- About画面 -->
    <AboutPage
      v-if="isAboutPageOpen"
      @close="closeAboutPage"
    />
    
    <!-- 利用規約確認モーダル -->
    <TermsModal
      :isOpen="isTermsModalOpen"
      @accept="acceptTerms"
    />
    
    <!-- プライバシーポリシーモーダル -->
    <PrivacyPolicyModal
      v-if="isPrivacyPolicyOpen"
      @close="closePrivacyPolicy"
    />
    
    <!-- TOP50モーダル -->
    <Top50Modal
      :show="showTop50Modal"
      :items="top50Items"
      :category="selectedCategory"
      :category-name="categoryNames[selectedCategory]"
      @close="closeTop50Modal"
      @item-click="handleTop50ItemClick"
    />
    
    <!-- バナードック -->
    <BannerDock 
      v-if="showBannerDock"
      :banners="bannerItems"
      :current-category="selectedCategory"
      :category-names="categoryNames"
      :mobile-breakpoint="740"
      @banner-click="handleBannerClick"
      @top50-click="showTop50Modal = true"
      @category-select="handleCategorySelect"
    />
  </div>
</template>

<script>
// App.vueの修正箇所

import BookmarkTile from './components/TileFixed.vue';  // 修正版のタイルコンポーネントを使用
import Draggable from 'vuedraggable';
import BookmarkModal from './components/BookmarkModal.vue';
import AboutPage from './components/AboutPage.vue';
import TermsModal from './components/TermsModal.vue';
import PrivacyPolicyModal from './components/PrivacyPolicyModal.vue';
import Top50Modal from './components/Top50Modal.vue';
import BannerDock from './components/BannerDock.vue';

export default {
  name: 'App',
  components: {
    BookmarkTile,
    draggable: Draggable,
    BookmarkModal,
    AboutPage,
    TermsModal,
    PrivacyPolicyModal,
    Top50Modal,
    BannerDock
  },
  data() {
    return {
      searchQuery: '',
      searchEngine: 'google',
      isMenuOpen: false,
      isDragging: false,
      bookmarks: [], // 空の配列で初期化
      isModalOpen: false,
      currentEditTile: null,
      isAboutPageOpen: false,
      isMobile: false,
      touchStartTime: 0,
      touchTimeout: null,
      currentTile: null,
      processingTap: false,
      lastMobileTapTime: 0,
      processingMobileTap: false,
      tileCount: 90,
      isTermsModalOpen: false,
      isPrivacyPolicyOpen: false,
      // リンク処理関連の変数
      isProcessingLink: false,   // リンク処理中かどうかのフラグ
      lastProcessedUrl: null,    // 最後に処理したURL
      lastLinkTime: 0,           // 最後にリンクを処理した時間
      // バナー広告とTop50モーダル関連のデータ
      showBannerAd: true,
      showTop50Modal: false,
      selectedCategory: 'books',
      categoryNames: {
        books: '本・マンガ',
        comics: 'コミック',
        games: 'ゲーム',
        kitchen: 'キッチン',
        electronics: '家電＆カメラ',
        fashion: 'ファッション',
        food: '食品',
        toys: 'おもちゃ',
        sports: 'アウトドア',
        baby: 'ﾍﾞﾋﾞｰ・ﾏﾀﾆﾃｨ'
      },
      // カテゴリとJSONファイル名のマッピング
      categoryJsonMap: {
        comics: 'comics.json',
        games: 'games.json',
        kitchen: 'kitchen.json',
        electronics: 'electronics.json',
        fashion: 'fashion.json',
        books: 'books.json',
        food: 'food.json',
        toys: 'toys.json',
        sports: 'sports.json',
        baby: 'baby.json'
      },
      apiBaseUrl: 'https://unxo.net/api/data/',
      top50Items: [], // TOP50の商品データ
      // バナードック関連のデータ
      showBannerDock: false, // 初期値をfalseに変更（ページ読み込み後に表示）
      bannerItems: [] // バナードックに表示するアイテム
    }
  },
  created() {
    // 利用規約への同意を確認
    this.checkTermsAgreement();
    
    this.bookmarks = this.initializeBookmarks();
    
    // DOMの更新を待ってタイルの数を確認 - 不要なtiles変数を削除
    this.$nextTick(() => {
      document.querySelectorAll('.tile'); // 変数代入を削除
    });
  },
  methods: {
    // デバイスのタイプをチェックして適切にisMobileフラグを設定
    checkDeviceType() {
      const screenWidth = window.innerWidth;
      const tabletMinWidth = 740;
      
      // 740px以上をPC表示、それ未満をモバイル表示とする
      this.isMobile = screenWidth < tabletMinWidth;
    },
    
    // 利用規約への同意状態を確認する
    checkTermsAgreement() {
      // cookieから同意状態を確認
      const hasAgreed = this.getCookie('bkms_terms_agreed') === 'true';
      
      // ローカルストレージからも確認（バックアップ）
      const localStorageAgreement = localStorage.getItem('bkms_terms_agreed') === 'true';
      
      // ブックマークJSONが存在するか確認
      const hasSavedBookmarks = localStorage.getItem('bookmarks') !== null;
      
      // どれかの方法で同意済みか、すでにJSONデータが存在する場合は利用可能に
      if (hasAgreed || localStorageAgreement || hasSavedBookmarks) {
      this.isTermsModalOpen = false;
      } else {
      // 未同意でデータもない場合は利用規約ポップアップを表示
      this.isTermsModalOpen = true;
      }
    },
    
    // Cookie取得用のヘルパー関数
    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : '';
    },
    
    // Cookie設定用のヘルパー関数
    setCookie(name, value, daysToExpire = 365) {
      const date = new Date();
      date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
      const expires = "expires=" + date.toUTCString();
      document.cookie = name + "=" + value + ";" + expires + ";path=/";
    },
    
    // バナー広告クリック時の処理
    handleBannerClick(data) {
      // データを確認
      console.log('[App] バナークリックイベント受信:', data);
      
      // URLがある場合は開く
      if (data && data.url) {
        // リンク処理の重複を防止（最後に処理したURLと同じ場合は短時間で再処理しない）
        const now = Date.now();
        if (data.url === this.lastProcessedUrl && now - this.lastLinkTime < 1000) {
          console.log('[App] 同じURLを短時間で連続して開こうとしました - 無視します:', data.url);
          return;
        }
        
        // リンク処理中フラグを設定
        this.lastProcessedUrl = data.url;
        this.lastLinkTime = now;
        this.isProcessingLink = true;
        
        try {
          console.log('[App] バナーのURLを開きます:', data.url);
          // 新しいタブでリンクを開く
          window.open(data.url, '_blank', 'noopener,noreferrer');
        } catch (error) {
          console.error('[App] URLを開く際にエラーが発生しました:', error);
        } finally {
          // 1秒後に処理フラグをリセット
          setTimeout(() => {
            this.isProcessingLink = false;
            console.log('[App] リンク処理フラグをリセットしました');
          }, 1000);
        }
      } else {
        console.warn('[App] バナーにURLがないか、データが不正です');
      }
    },
    
    // TOP50モーダルを閉じる
    closeTop50Modal() {
      this.showTop50Modal = false;
    },
    
    // TOP50アイテムをクリックした時の処理
    handleTop50ItemClick(data) {
      console.log('TOP50アイテムがクリックされました', data);
      // アイテムクリック時の処理をここに追加
      if (data.url) {
        window.open(data.url, '_blank');
      }
    },
    
    // APIからTOP50データを取得するメソッド
    async fetchTop50Data() {
      try {
        const category = this.selectedCategory;
        const jsonFileName = this.categoryJsonMap[category];
        
        if (!jsonFileName) {
          console.error('カテゴリに対応するJSONファイルがありません:', category);
          return;
        }
        
        const response = await fetch(`${this.apiBaseUrl}${jsonFileName}`);
        
        if (!response.ok) {
          throw new Error(`API呼び出しエラー: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log(`${category}カテゴリのデータを取得しました:`, data ? (Array.isArray(data) ? data.length : typeof data) : 'undefined');
        
        // データがない場合やオブジェクト形式の場合の処理
        if (!data) {
          throw new Error('データが取得できませんでした');
        }
        
        let itemsArray = [];
        
        // データが配列の場合
        if (Array.isArray(data)) {
          itemsArray = data;
        } 
        // データがオブジェクトで、items配列を持つ場合
        else if (data.items && Array.isArray(data.items)) {
          itemsArray = data.items;
        }
        // データがオブジェクトで、プロパティとして商品データを持つ場合
        else if (typeof data === 'object') {
          // オブジェクトの値を配列に変換（キーを除く）
          itemsArray = Object.values(data).filter(item => item && typeof item === 'object');
        }
        
        // 取得したデータを加工してTOP50アイテムを設定
        this.top50Items = itemsArray.map(item => ({
          id: item.id || Math.random().toString(36).substr(2, 9),
          title: item.title || item.name || '商品名なし',
          price: item.price || '',
          discountPercent: item.discountPercent || 0,
          image: item.image || item.imageUrl || '',
          url: item.url || '#'
        })).slice(0, 50); // 最大50件まで
      } catch (error) {
        console.error('データ取得エラー:', error);
        // エラー時にはダミーデータを使用
        this.generateSampleTop50Data();
      }
    },
    
    // APIからバナーアイテムデータを取得するメソッド
    async fetchBannerItems() {
      try {
        const category = this.selectedCategory;
        const jsonFileName = this.categoryJsonMap[category];
        
        if (!jsonFileName) {
          console.error('カテゴリに対応するJSONファイルがありません:', category);
          return;
        }
        
        const response = await fetch(`${this.apiBaseUrl}${jsonFileName}`);
        
        if (!response.ok) {
          throw new Error(`API呼び出しエラー: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log(`${category}カテゴリのバナーデータを取得しました:`, data ? (Array.isArray(data) ? data.length : typeof data) : 'undefined');
        
        // データがない場合やオブジェクト形式の場合の処理
        if (!data) {
          throw new Error('データが取得できませんでした');
        }
        
        let itemsArray = [];
        
        // データが配列の場合
        if (Array.isArray(data)) {
          itemsArray = data;
        } 
        // データがオブジェクトで、items配列を持つ場合
        else if (data.items && Array.isArray(data.items)) {
          itemsArray = data.items;
        }
        // データがオブジェクトで、プロパティとして商品データを持つ場合
        else if (typeof data === 'object') {
          // オブジェクトの値を配列に変換（キーを除く）
          itemsArray = Object.values(data).filter(item => item && typeof item === 'object');
        }
        
        // 取得したデータから最初の16件をバナーアイテムとして使用（8件から16件に増加）
        this.bannerItems = itemsArray.slice(0, 16).map((item, index) => {
          // バナーの色をインデックスに基づいて決定
          const colors = [
            'linear-gradient(135deg, #ff7eb3, #ff758c)',
            'linear-gradient(135deg, #7eb6ff, #5f7fff)',
            'linear-gradient(135deg, #90ee90, #32cd32)',
            'linear-gradient(135deg, #ffcf4d, #ffac33)',
            'linear-gradient(135deg, #ff9a8b, #ff6a88)',
            'linear-gradient(135deg, #a18cd1, #fbc2eb)',
            'linear-gradient(135deg, #84fab0, #8fd3f4)',
            'linear-gradient(135deg, #f6d365, #fda085)',
            'linear-gradient(135deg, #43e97b, #38f9d7)',
            'linear-gradient(135deg, #fa709a, #fee140)',
            'linear-gradient(135deg, #667eea, #764ba2)',
            'linear-gradient(135deg, #89f7fe, #66a6ff)',
            'linear-gradient(135deg, #6e45e2, #88d3ce)',
            'linear-gradient(135deg, #df89b5, #bfd9fe)',
            'linear-gradient(135deg, #c471f5, #fa71cd)',
            'linear-gradient(135deg, #48c6ef, #6f86d6)'
          ];
          
          return {
            productName: item.title || item.name || '商品名なし',
            discount: item.discountPercent ? `${item.discountPercent}%OFF` : '',
            backgroundImage: item.image || item.imageUrl || '',
            backgroundColor: colors[index % colors.length],
            url: item.url || '#'
          };
        });
      } catch (error) {
        console.error('バナーデータ取得エラー:', error);
        // エラー時には既存のサンプルバナーデータを使用
        this.generateSampleBannerItems();
      }
    },
    
    // サンプルのTOP50データを生成
    generateSampleTop50Data() {
      // サンプルデータ生成（本番環境ではAPIから取得する）
      const sampleData = [];
      
      // 50個のダミーデータを作成
      for (let i = 1; i <= 50; i++) {
        sampleData.push({
          id: i,
          title: `サンプル商品 ${i}`,
          price: `¥${Math.floor(Math.random() * 10000)}`,
          discountPercent: Math.random() > 0.7 ? Math.floor(Math.random() * 50) : 0,
          image: i % 3 === 0 ? `https://picsum.photos/seed/${i}/200/200` : '', // 3つに1つは画像あり
          url: `https://example.com/item/${i}`
        });
      }
      
      this.top50Items = sampleData;
    },
    
    // 利用規約への同意処理
    acceptTerms() {
      // ポップアップを閉じる
      this.isTermsModalOpen = false;
    },
    
    initializeBookmarks() {
      // ローカルストレージからデータを取得する試み
      const savedBookmarks = localStorage.getItem('bookmarks');
      if (savedBookmarks) {
        try {
          const parsedBookmarks = JSON.parse(savedBookmarks);
          // 保存されたデータを使用する
          if (Array.isArray(parsedBookmarks) && parsedBookmarks.length === this.tileCount) {
            return parsedBookmarks;
          }
        } catch (e) {
          console.error('ブックマークの読み込みに失敗しました', e);
        }
      }
      
      // 保存されたデータがない場合や無効な場合は新しく作成
      const bookmarks = [];
      
      // すべてのタイルを空のタイルとして初期化
      for (let i = 0; i < this.tileCount; i++) {
        bookmarks.push(this.createEmptyTile(i));
      }
      
      return bookmarks;
    },
    
    createEmptyTile(id) {
      return {
        id: id,
        name: '登録',  // すべて「登録」に統一
        iconUrl: '/bookmark.svg',
        iconColor: '#AAA',
        color: '#f0f0f0',
        url: '',
        isEmpty: true
      };
    },
    
    executeSearch() {
      if (!this.searchQuery.trim()) return;
      
      let url;
      switch (this.searchEngine) {
        case 'google':
          url = `https://www.google.com/search?q=${encodeURIComponent(this.searchQuery)}`;
          break;
        case 'bing':
          url = `https://www.bing.com/search?q=${encodeURIComponent(this.searchQuery)}`;
          break;
        case 'chatgpt':
          url = `https://chat.openai.com/?q=${encodeURIComponent(this.searchQuery)}`;
          break;
      }
      
      window.open(url, '_blank');
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    // ドラッグ関連のメソッド
    onDragStart() {
      this.isDragging = true;
      // ドラッグ中はメニューを閉じる
      this.isMenuOpen = false;
    },
    
    onDragEnd() {
      // 少し遅延してからドラッグフラグをリセット（クリック誤認防止）
      setTimeout(() => {
        this.isDragging = false;
      }, 10);
      // ドラッグ終了時のブックマークの順序を保存
      this.saveBookmarksOrder();
    },
    saveBookmarksOrder() {
      // ローカルストレージなどにブックマークの順序を保存する
      localStorage.setItem('bookmarks', JSON.stringify(this.bookmarks));
      // console.log('ブックマークの順序を保存しました', this.bookmarks);
    },
    // メニューを閉じるためのクリックイベントハンドラをドキュメントに追加
    setupCloseMenuOnOutsideClick() {
      document.addEventListener('click', this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      const burgerMenu = document.querySelector('.burger-menu-container');
      if (burgerMenu && !burgerMenu.contains(event.target) && this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    },
    
    closeModal() {
      this.isModalOpen = false;
      this.currentEditTile = null;
    },
    
    saveBookmark(bookmark) {
      if (!this.currentEditTile) return;
      
      // 既存のタイルを編集する場合
      if (this.currentEditTile.id >= 0) {
        const index = this.bookmarks.findIndex(b => b.id === this.currentEditTile.id);
        if (index !== -1) {
          this.bookmarks[index] = {
            ...this.bookmarks[index],
            name: bookmark.name,
            url: bookmark.url,
            iconUrl: bookmark.iconUrl,
            color: bookmark.color,
            isEmpty: false
          };
        }
      } else {
        // 新規追加の場合、最初の空のタイルを探す
        for (let i = 1; i < this.bookmarks.length; i++) {
          if (this.bookmarks[i].isEmpty) {
            this.bookmarks[i] = {
              ...this.bookmarks[i],
              name: bookmark.name,
              url: bookmark.url,
              iconUrl: bookmark.iconUrl,
              color: bookmark.color,
              isEmpty: false
            };
            break;
          }
        }
      }
      
      // 保存処理
      this.saveBookmarksOrder();
      this.closeModal();
    },
    
    // タイル編集機能
    editTile(tile) {
      // 空のタイルもしくは設定済みのタイルをクリックした時の動作
      if (tile.isEmpty) {
        // console.log(`${tile.id} 番のタイルを設定します`);
        // ここで編集モーダルを表示する
      } else if (tile.url) {
        // URLが設定されている場合は開く
        window.open(tile.url, '_blank');
      }
    },
    
    // タイルをリセット（右クリック時）
    resetTile(tile) {
      // 空のタイルまたは特別なタイルの場合は何もしない
      if (tile.isEmpty) return;
      
      // console.log(`${tile.id} 番のタイルの右クリックイベント`);
      
      // 確認ダイアログを表示
      if (confirm(`「${tile.name}」のブックマークを削除しますか？`)) {
        // タイルを空にする
        const index = this.bookmarks.findIndex(b => b.id === tile.id);
        if (index !== -1) {
          // タイルを初期状態（空の状態）に戻す
          this.bookmarks[index] = this.createEmptyTile(tile.id);
          
          // 変更を保存
          this.saveBookmarksOrder();
        }
      }
    },
    
    // すべてのタイルをリセット
    resetAllTiles() {
      if (confirm('すべてのブックマークを削除しますか？\nこの操作は元に戻せません。')) {
        // ブックマークをすべて空のタイルに初期化
        const newBookmarks = [];
        for (let i = 0; i < this.tileCount; i++) {
          newBookmarks.push(this.createEmptyTile(i));
        }
        
        // ブックマーク配列を更新
        this.bookmarks = newBookmarks;
        
        // ストレージに保存
        this.saveBookmarksOrder();
        
        // メニューを閉じる
        this.isMenuOpen = false;
        
        // 完了メッセージ
        alert('すべてのブックマークを削除しました');
      }
    },
    
    // ローカルストレージからブックマークを読み込む
    loadBookmarks() {
      // initializeBookmarks()に移動したため、この関数は空にする
      // 既存のブックマークデータはinitializeBookmarks()で処理されるようになったため
    },
    // リンク処理関連の変数を追加
    
    handleTileClick(elementOrData) {
      // データフォーマットの正規化（オブジェクトまたはプロパティ）
      const element = elementOrData.url ? elementOrData : elementOrData;
      const url = element.url;
      const isEmpty = element.isEmpty === true;
      const isErrorFallback = element.errorFallback === true; // エラー時のフォールバックフラグ
      
      // 静的な開発用フラグ
      const DEBUG = true;
      const INTERVAL_MS = 1500; // 連続クリック防止の間隔（ミリ秒）
      
      // タイルクリックの重複検出用のカウンター
      this.handleTileClickCounter = (this.handleTileClickCounter || 0) + 1;
      const callId = this.handleTileClickCounter;
      
      if (DEBUG) console.log(`[App] #${callId} handleTileClick 呼び出し - データ:`, element);
      
      // 現在時刻を取得
      const now = Date.now();
      
      // 連続呼び出し検出 - 短時間での同一URLの重複呼び出し
      if (url && url === this.lastProcessedUrl && now - this.lastLinkTime < 500) {
        // 重複呼び出しを検出
        if (DEBUG) console.log(`[App] #${callId} 重複呼び出し検出 - スキップします`);
        return;
      }
      
      // ドラッグ中は何もしない（PCのみ関連）
      if (!this.isMobile && this.isDragging) {
        if (DEBUG) console.log(`[App] #${callId} ドラッグ中なのでクリック無視`);
        return;
      }
    
      // 空のタイルをクリックした場合は編集モーダルを表示
      if (isEmpty) {
        if (DEBUG) console.log(`[App] #${callId} 空のタイル - モーダル表示`);
        this.isModalOpen = true;
        this.currentEditTile = element;
        return;
      }
    
      // PC環境では処理をスキップ（TileFixedが既に処理済み）- エラーフォールバック以外
      if (!this.isMobile && !isErrorFallback) {
        if (DEBUG) console.log(`[App] #${callId} PC環境でエラーフォールバックなし - スキップします`);
        return;
      }
      
      // モバイルまたはエラーフォールバック時のみリンクを開く
      if (url && (this.isMobile || isErrorFallback)) {
        // 既に処理中の場合はスキップ
        if (this.isProcessingLink) {
          if (DEBUG) console.log(`[App] #${callId} 既に処理中のためスキップ`);
          return;
        }
        
        // この呼び出しを最後の処理として記録
        this.lastProcessedUrl = url;
        this.lastLinkTime = now;
        this.isProcessingLink = true;
        
        if (DEBUG) {
          const mode = this.isMobile ? 'モバイル' : 'エラーフォールバック';
          console.log(`[App] #${callId} ${mode}向けにリンクを開きます: ${url}`);
        }
        
        try {
          // リンクを開く
          window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error) {
          console.error(`[App] #${callId} URLを開く際にエラー:`, error);
        } finally {
          // 処理完了後にフラグをリセット
          setTimeout(() => {
            if (DEBUG) console.log(`[App] #${callId} リンク処理フラグをリセット`);
            this.isProcessingLink = false;
          }, INTERVAL_MS);
        }
      }
    },
    
    // タイルコンテンツの編集（メニューから）
    editTileContent(element) {
      // 既存のタイルの内容編集用モーダルを表示
      this.isModalOpen = true;
      this.currentEditTile = element;
    },
    
    // タイルコンテンツのクリア（メニューから）
    clearTileContent(element) {
      // 既に空のタイルの場合は何もしない
      if (element.isEmpty) return;
      
      // 確認ダイアログ
      if (confirm(`「${element.name}」のブックマークを削除しますか？`)) {
        // タイルを空にする
        const index = this.bookmarks.findIndex(b => b.id === element.id);
        if (index !== -1) {
          // タイルを初期状態（空の状態）に戻す
          this.bookmarks[index] = this.createEmptyTile(element.id);
          
          // 変更を保存
          this.saveBookmarksOrder();
        }
      }
    },
    // データエクスポート機能
    exportData() {
      try {
        // 以前のフィルタリングを削除し、すべてのブックマークをエクスポート
        // 空のタイルも含めて90個すべてを出力する
        const jsonData = JSON.stringify(this.bookmarks, null, 2);
        
        const now = new Date();
        const dateString = now.toISOString().slice(0, 10);
        const fileName = `BKMS_export_${dateString}.json`;
        
        const dataURI = 'data:application/json;charset=utf-8,' + encodeURIComponent(jsonData);
        
        const link = document.createElement('a');
        link.setAttribute('href', dataURI);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert('ブックマークデータのエクスポートが完了しました');
        this.isMenuOpen = false;
      } catch (error) {
        console.error('データエクスポート中にエラーが発生しました', error);
        alert('データのエクスポート中にエラーが発生しました');
      }
    },
    // インポートダイアログを表示する
    showImportDialog() {
      // 隠しファイル入力をクリック
      this.$refs.fileInput.click();
      // メニューを閉じる
      this.isMenuOpen = false;
    },
    
    // ファイルが選択されたときの処理
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // JSONデータをパース
          const importedData = JSON.parse(e.target.result);
          
          // インポートするデータの検証
          if (!Array.isArray(importedData)) {
            throw new Error('インポートされたデータが正しい形式ではありません');
          }
          
          // インポート確認ダイアログ
          if (confirm('ブックマークデータをインポートすると、現在のデータは上書きされます。続行しますか？')) {
            this.importBookmarks(importedData);
          }
        } catch (error) {
          console.error('インポート中にエラーが発生しました', error);
          alert('データのインポートに失敗しました：' + error.message);
        }
        
        // ファイル入力をリセット（同じファイルを再度選択可能にする）
        event.target.value = '';
      };
      
      reader.onerror = () => {
        alert('ファイルの読み込み中にエラーが発生しました');
        // ファイル入力をリセット
        event.target.value = '';
      };
      
      // ファイルを読み込む
      reader.readAsText(file);
    },
    
    // ブックマークデータをインポートする
    importBookmarks(importedData) {
      try {
        // インポートされたデータが正しい形式かチェック
        const isValidData = importedData.every(bookmark => {
          return (
            typeof bookmark.id === 'number' &&
            typeof bookmark.name === 'string' && 
            typeof bookmark.iconUrl === 'string'
          );
        });
        
        if (!isValidData) {
          throw new Error('インポートされたデータの形式が正しくありません');
        }
        
        // インポートされたデータを90個に調整
        let newBookmarks = [];
        
        // データをIDを0から振り直して追加し、isEmptyフラグを確認する
        importedData.forEach((bookmark, index) => {
          if (index < this.tileCount) {
            newBookmarks.push({
              ...bookmark,
              id: index,
              // importされたデータにisEmptyフラグがある場合はそれを尊重、
              // なければfalseに設定（後方互換性のため）
              isEmpty: bookmark.isEmpty === true
            });
          }
        });
        
        // 足りない部分を空のタイルで補完（90個になるまで）
        const remaining = this.tileCount - newBookmarks.length;
        for (let i = 0; i < remaining; i++) {
          const id = newBookmarks.length;
          newBookmarks.push(this.createEmptyTile(id));
        }
        
        // ブックマークを更新
        this.bookmarks = newBookmarks;
        
        // ストレージに保存
        this.saveBookmarksOrder();
        
        alert('ブックマークデータのインポートが完了しました');
      } catch (error) {
        console.error('インポート処理中にエラーが発生しました', error);
        alert('データのインポートに失敗しました：' + error.message);
      }
    },
    // アバウトページを表示する
    showAboutPage() {
      this.isAboutPageOpen = true;
      this.isMenuOpen = false; // メニューを閉じる
    },
    
    // アバウトページを閉じる
    closeAboutPage() {
      this.isAboutPageOpen = false;
    },
    
    // プライバシーポリシーを表示する
    showPrivacyPolicy() {
      this.isPrivacyPolicyOpen = true;
      this.isMenuOpen = false; // メニューを閉じる
    },
    
    // プライバシーポリシーを閉じる
    closePrivacyPolicy() {
      this.isPrivacyPolicyOpen = false;
    },
    
    handleTileContextMenu(element) {
      // 空のタイルの場合は何もしない
      if (element.isEmpty) return;
      
      // 確認ダイアログを表示
      if (confirm(`「${element.name}」のブックマークを削除しますか？`)) {
        // タイルを空にする
        const index = this.bookmarks.findIndex(b => b.id === element.id);
        if (index !== -1) {
          // タイルを初期状態（空の状態）に戻す
          this.bookmarks[index] = this.createEmptyTile(element.id);
          
          // 変更を保存
          this.saveBookmarksOrder();
        }
      }
    },
    // カテゴリ選択時の処理
    handleCategorySelect(category) {
      this.selectedCategory = category;
      // 選択したカテゴリをCookieに保存（有効期限365日）
      this.setCookie('bkms_selected_category', category, 365);
      // カテゴリが変更されたら、APIからデータを再取得する
      this.fetchTop50Data();
      this.fetchBannerItems();
    },
    
    // サンプルのバナードックアイテムを生成
    generateSampleBannerItems() {
      // バナードック用のサンプルデータ
      const sampleBanners = [
        {
          productName: '春のセール',
          discount: '最大50%OFF',
          backgroundColor: 'linear-gradient(135deg, #ff7eb3, #ff758c)',
          url: 'https://example.com/spring-sale'
        },
        {
          productName: '新商品',
          discount: 'NEW',
          backgroundColor: 'linear-gradient(135deg, #7eb6ff, #5f7fff)',
          url: 'https://example.com/new-items'
        },
        {
          productName: '限定コレクション',
          discount: '数量限定',
          backgroundColor: 'linear-gradient(135deg, #90ee90, #32cd32)',
          url: 'https://example.com/limited'
        },
        {
          productName: 'クーポン',
          discount: '10%OFF',
          backgroundColor: 'linear-gradient(135deg, #ffcf4d, #ffac33)',
          url: 'https://example.com/coupon'
        },
        {
          productName: 'タイムセール',
          discount: '24時間限定',
          backgroundColor: 'linear-gradient(135deg, #ff9a8b, #ff6a88)',
          url: 'https://example.com/time-sale'
        },
        {
          productName: 'ポイント2倍',
          discount: '今週末まで',
          backgroundColor: 'linear-gradient(135deg, #a18cd1, #fbc2eb)',
          url: 'https://example.com/points'
        },
        {
          productName: '送料無料',
          discount: '3,000円以上',
          backgroundColor: 'linear-gradient(135deg, #84fab0, #8fd3f4)',
          url: 'https://example.com/free-shipping'
        },
        {
          productName: 'プレミアム会員',
          discount: '初月無料',
          backgroundColor: 'linear-gradient(135deg, #f6d365, #fda085)',
          url: 'https://example.com/premium'
        }
      ];
      
      this.bannerItems = sampleBanners;
    },
  },
  mounted() {
    document.title = 'BKMS：ブクマす';
    this.setupCloseMenuOnOutsideClick();
    
    // デバイスタイプを初回チェック
    this.checkDeviceType();
    
    // 画面サイズ変更時にデバイスタイプを再チェック
    window.addEventListener('resize', this.checkDeviceType);
    
    // Cookieからカテゴリを読み込む
    const savedCategory = this.getCookie('bkms_selected_category');
    if (savedCategory && this.categoryNames[savedCategory]) {
      // 有効なカテゴリが保存されていれば、それを使用
      this.selectedCategory = savedCategory;
    }
    
    // APIからデータを取得
    this.fetchTop50Data();
    this.fetchBannerItems();
    
    // ページの他の部分がロードされた後にバナードックを表示する
    // Performance Timerを使用して、ページのロード完了を検知
    window.addEventListener('load', () => {
      // loadイベント後に少し遅延させてバナードックを表示
      setTimeout(() => {
        this.showBannerDock = true;
      }, 800); // 800ms後に表示（他の要素が完全に描画された後）
    });
    
    // ページが既にロードされている場合のフォールバック処理
    if (document.readyState === 'complete') {
      setTimeout(() => {
        this.showBannerDock = true;
      }, 800);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('resize', this.checkDeviceType);
  }
}
</script>

<style>
body {
  margin: 0;
  padding: 0;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  background-color: #f5f5f5;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.header {
  background-color: #e9d0ca;
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: none;
}

.app-title {
  margin: 0;
  font-size: 1.8rem;
}

/* バーガーメニューのスタイル */
.burger-menu-container {
  position: relative;
}

.burger-button {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.burger-line {
  width: 100%;
  height: 3px;
  background-color: white;
  border-radius: 3px;
  transition: all 0.3s ease;
}

/* ドロップダウンメニューのスタイル */
.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 200px;
  margin-top: 10px;
  z-index: 100;
  overflow: hidden;
}

.menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  color: #333;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.menu-item:hover {
  background-color: #f1f1f1;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  padding-bottom: 180px; /* 広告エリアと重ならないように下部に余白を追加 */
}

/* 統合された検索ボックスのスタイル */
.search-container {
  width: 100%;
  max-width: 600px;
  margin-bottom: 2rem;
}

.integrated-search-box {
  display: flex;
  width: 100%;
  border-radius: 50px; /* 角を完全な丸に変更 */
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  overflow: hidden;
  border: 1px solid #ccc;
}

/* 左側のセレクトボックスの左端も丸くする */
.search-engine-select-container {
  position: relative;
  width: 120px;
  background-color: #f8f8f8;
  border-top-left-radius: 50px; /* 左上を丸く */
  border-bottom-left-radius: 50px; /* 左下を丸く */
}

/* 右側の検索入力欄の右端も丸くする */
.search-input {
  flex: 1;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  border: none;
  outline: none;
  transition: all 0.2s ease;
  border-top-right-radius: 50px; /* 右上を丸く */
  border-bottom-right-radius: 50px; /* 右下を丸く */
}

/* フォーカス時の統合ボックスのスタイル */
.integrated-search-box:focus-within {
  border-color: #ccb6b0;
  box-shadow: 0 0 0 2px rgba(233, 208, 202, 0.25);
}

.tiles-container-wrapper {
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: center;
}

/* タイルの基本スタイル - 全デバイス共通 */
.tile {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background-color: #f0f0f0;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  -webkit-touch-callout: none;
}

/* タイル配置のグリッド - 全デバイス共通のベース設定 */
.tiles-container {
  display: grid;
  padding: 15px;
  width: 100%;
  box-sizing: border-box;
}

/* ドラッグ中のゴーストタイル */
.ghost-tile {
  opacity: 0.5;
  background: #c8ebfb;
  border: 1px dashed #e9d0ca;
  box-shadow: none;
}

/* モバイル向け調整 - 767px以下 */
@media (max-width: 767px) {
  /* 検索関連 */
  .search-container {
    padding: 0 1rem;
  }
  
  .search-engine-select-container {
    width: 90px;
  }
  
  .search-engine-select, 
  .search-input {
    font-size: 0.9rem;
    padding: 0.7rem 0.5rem;
  }
  
  .select-arrow {
    right: 8px;
    font-size: 0.7rem;
  }
  
  /* タイル関連 */
  .tiles-container {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: 16px;
    padding: 10px;
  }
  
  .tile {
    width: 90px;
    height: 80px;
    transition: transform 0.1s ease, opacity 0.1s ease;
  }
  
  .tile:active {
    transform: scale(0.95);
    opacity: 0.8;
  }
  
  /* モバイルではメニューボタン非表示 */
  .tile .tile-menu-button {
    display: none !important;
  }
  
  .banner-container {
    margin-bottom: 1rem;
  }
}

/* PC向けのスタイル確保 - 768px以上 */
@media (min-width: 768px) {
  /* タイル関連 */
  .tiles-container {
    grid-template-columns: repeat(auto-fill, minmax(100px, 100px));
    gap: 30px;
  }
  
  /* PCではメニューボタンを表示 */
  .tile .tile-menu-button {
    display: block !important;
  }
}

/* 大型ディスプレイ向け - 900px以上 */
@media (min-width: 900px) {
  .tiles-container {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    max-width: 1200px;
  }
}

/* 左側のセレクトボックスの左端も丸くする */
.search-engine-select-container {
  position: relative;
  width: 120px;
  height: 100%;
  background-color: #f8f8f8;
  border-right: 1px solid #ddd;
  border-top-left-radius: 50px;
  border-bottom-left-radius: 50px;
  display: flex;
  align-items: center;
}

/* 検索エンジン選択のスタイル調整 */
.search-engine-select {
  width: 100%;
  height: 100%;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  appearance: none;
  outline: none;
}

.select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
  font-size: 0.8rem;
}

/* モーダル表示中の背景スクロール防止 */
body.modal-open {
  overflow: hidden;
}

/* インポート機能に関連するスタイル追加 */
.import-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.import-dialog {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.import-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.import-buttons button {
  margin-left: 10px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.import-cancel {
  background-color: #f1f1f1;
  color: #333;
}

.import-confirm {
  background-color: #e9d0ca;
  color: white;
}
</style>
